import React, { useCallback, useMemo } from "react";
import { SummaryStep } from "./components/SummaryStep";
import { usePatchRecipientList } from "../../hooks";
import { StepHelpers } from "@shared/components/Stepper";
import { useNavigate } from "react-router-dom";
import { toast, useToast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { logEvent } from "@shared/analytics/analytics";
import { useQueryClient } from "@tanstack/react-query";

type Props = {
  stepHelpers?: StepHelpers;
  onPrevious?: any;
};

const NewListStepThree = ({ onPrevious, stepHelpers }: Props) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const data = stepHelpers.getAllStepData?.() || [];
  const navigate = useNavigate();
  const { mutate: updateRecipientList } = usePatchRecipientList(data[1]?.id, {
    onSuccess: () => {},
  });

  const summaryData = [
    {
      title: "List Details",
      fields: [
        { label: "Name", value: data[0]?.name },
        {
          label: "Contact Type",
          value: data[0]?.recipientType?.replace(/_/g, " "),
        },
        { label: "Description", value: data[0]?.description || "N/A" },
        {
          label: "Tags",
          value: (
            <div className="flex flex-wrap gap-1">
              {(data[0]?.tags || []).map((tag: string, i: number) => (
                <span
                  key={i}
                  className="bg-white px-2 py-0.5 rounded-full text-xs border"
                >
                  {tag}
                </span>
              ))}
            </div>
          ),
        },
      ],
    },
    {
      title: "Filter Configuration",
      fields: [
        { label: "Source Type", value: data[1]?.sourceType },
        {
          label: "Filter Type",
          value: data[1]?.filterType
            ? data[1].filterType === "CATEGORY"
              ? "Category Based"
              : "Location Based"
            : "N/A",
        },
        ...(data[1]?.filterType === "CATEGORY"
          ? [
              {
                label: "Category",
                value: data[1]?.category?.length
                  ? data[1].category.join(", ")
                  : "All",
              },
              {
                label: "Subcategory",
                value: data[1]?.subCategory?.length
                  ? data[1].subCategory.join(", ")
                  : "All",
              },
            ]
          : []),
        ...(data[1]?.filterType === "TERRITORY"
          ? [
              {
                label: "Area",
                value: data[1]?.area?.length ? data[1].area.join(", ") : "All",
              },
              {
                label: "Subarea",
                value: data[1]?.subArea?.length
                  ? data[1].subArea.join(", ")
                  : "All",
              },
              {
                label: "Premise",
                value: data[1]?.premise?.length
                  ? data[1].premise.join(", ")
                  : "All",
              },
            ]
          : []),
      ],
    },
    {
      title: "Estimated Results",
      fields: [
        {
          label: "",
          value: (
            <div className="space-y-1">
              <div className="text-base font-semibold">
                {data[1]?.sourceType === "STATIC"
                  ? data[1]?.recipientData?.recipientIds?.length || 0
                  : data[1]?.total_recipients || 0}{" "}
                contacts
              </div>
              <div className="text-xs text-muted-foreground">
                {data[1]?.sourceType === "STATIC"
                  ? "Selected contacts will be included in this list."
                  : "This is an estimated number of contacts that will match your criteria."}
              </div>
            </div>
          ),
        },
      ],
    },
  ];

  const handleSubmit = () => {
    let formData;

    if (data[1].sourceType === "STATIC") {
      formData = {
        filter_type: "STATIC",
        recipient_data: {
          recipient_ids: data[1].recipientData?.recipientIds || [], // Changed to recipient_ids to match API
        },
        is_draft: false,
      };
    } else if (data[1].sourceType === "DYNAMIC") {
      formData = {
        filter_type: "DYNAMIC",
        filters: {
          area: data[1].area || [],
          sub_area: data[1].subArea || [],
          premise: data[1].premise || [],
          category: data[1].category || [],
          subcategory: data[1].subCategory || [],
        },
        total_recipients: data[1]?.total_recipients || 0,
        is_draft: false,
      };
    }

    updateRecipientList(formData, {
      onSuccess: () => {
        logEvent(`Completed List Creation`, {
          listType: data[0].recipientType,
          listId: data[1].id,
          contactCount: data[1]?.total_recipients
        })
        stepHelpers.resetAllData();

        toast({
          title: "Success",
          description: "Recipient list updated successfully",
        });

        navigate("/communication-hub/lists");
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update recipient list",
          variant: "destructive",
        });
      },
    });
  };

  return (
    <div className="space-y-4">
      <SummaryStep summaryData={summaryData} />
      <div className="flex justify-between p-2">
        <Button
          onClick={onPrevious}
          className="mt-2 px-4 py-2 rounded bg-white text-black border hover:bg-white"
          variant="outline"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          className="mt-2 px-4 py-2 bg-primary text-white rounded"
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default NewListStepThree;
